import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import APIs from "../../../Constants/apis";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader";
import CustomModal from "../../../components/CustomComponents/modal/customModal";
import Pagination from "../../../components/CustomComponents/Pagination/pagination";
import FormInput from "../../../components/CustomComponents/Input/formInput";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast";
import useThemeColor from "../../../hooks/useThemeColor";
import useDarkMode from "../../../hooks/useDarkMode";
import { useProfileAndSettings } from "../../../hooks/useProfileAndSettings";
import { Panel, PanelBody, PanelHeader } from "../../../components/panel/panel";

const POST_ACTIONS = [
  {
    key: "payment",
    label: "Receive payment",
    hint: "Reduce On Account due",
    icon: "fa-money-bill-wave",
    bucket: "OnAccount",
    entryType: "OnAccountPayment",
    tone: "#198754",
  },
  {
    key: "charge",
    label: "Charge to account",
    hint: "Increase On Account due",
    icon: "fa-file-invoice-dollar",
    bucket: "OnAccount",
    entryType: "OnAccountCharge",
    tone: "#dc3545",
  },
  {
    key: "topup",
    label: "Top-up wallet",
    hint: "Add Store Credit",
    icon: "fa-plus-circle",
    bucket: "StoreCredit",
    entryType: "StoreCreditTopUp",
    tone: "#0d6efd",
  },
  {
    key: "redeem",
    label: "Redeem wallet",
    hint: "Spend Store Credit",
    icon: "fa-wallet",
    bucket: "StoreCredit",
    entryType: "StoreCreditRedeem",
    tone: "#fd7e14",
  },
  {
    key: "adjust",
    label: "Adjust balance",
    hint: "Ledger correction with reason",
    icon: "fa-sliders",
    bucket: "",
    entryType: "",
    tone: "#6f42c1",
  },
];

const CustomerWallet = () => {
  const { custGuid } = useParams();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const currencySymbols = { GBP: "£", USD: "$", EUR: "€", PKR: "Rs" };
  const currencySymbol = currencySymbols[compCurrency] || compCurrency || "";

  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "20", label: "20" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [bucketFilter, setBucketFilter] = useState("");
  const [search, setSearch] = useState("");

  const [postModalOpen, setPostModalOpen] = useState(false);
  const [postAction, setPostAction] = useState(null);
  const [postAmount, setPostAmount] = useState("");
  const [postNotes, setPostNotes] = useState("");
  const [postPaymentMethod, setPostPaymentMethod] = useState("Cash");
  const [adjustBucket, setAdjustBucket] = useState("OnAccount");
  const [adjustDirection, setAdjustDirection] = useState("increase");
  const [submitting, setSubmitting] = useState(false);

  const [reverseModalOpen, setReverseModalOpen] = useState(false);
  const [reverseEntry, setReverseEntry] = useState(null);
  const [reverseReason, setReverseReason] = useState("");

  const surface = isDarkMode ? "#1e1e1e" : "#ffffff";
  const surfaceAlt = isDarkMode ? "#252525" : "#f8f9fa";
  const borderColor = isDarkMode ? "#333" : "#e9ecef";
  const muted = isDarkMode ? "#9aa0a6" : "#6c757d";

  const formatMoney = (value) =>
    `${currencySymbol}${Number(value || 0).toFixed(2)}`;

  const fetchWallet = useCallback(async () => {
    if (!CompID || !custGuid) return;
    try {
      const response = await axiosInstance.get(APIs.GET_CUSTOMER_WALLET, {
        params: { CompID, Cust_Guid: custGuid, RecentLedgerCount: 5 },
      });
      setWallet(response.data?.data || null);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to load wallet");
      setWallet(null);
    }
  }, [CompID, custGuid]);

  const fetchLedger = useCallback(async () => {
    if (!CompID || !custGuid) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CUSTOMER_LEDGER, {
        params: {
          CompID,
          Cust_Guid: custGuid,
          Bucket: bucketFilter || undefined,
          Search: search || undefined,
          PageNumber: pageNumber,
          PageSize: Number(pageSize?.value || pageSize || 20),
          SortBy: "CreatedAt",
          SortOrder: "DESC",
        },
      });
      setLedger(response.data?.data || []);
      setTotalRecords(response.data?.totalRecords || 0);
      setTotalPages(response.data?.totalPages || 0);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to load ledger");
      setLedger([]);
    } finally {
      setLoading(false);
    }
  }, [CompID, custGuid, bucketFilter, search, pageNumber, pageSize]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchWallet(), fetchLedger()]);
  }, [fetchWallet, fetchLedger]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const openPostModal = (action) => {
    setPostAction(action);
    setPostAmount("");
    setPostNotes("");
    setPostPaymentMethod("Cash");
    setAdjustBucket("OnAccount");
    setAdjustDirection("increase");
    setPostModalOpen(true);
  };

  const handlePostSubmit = async () => {
    if (!postAction || !CompID || !custGuid) return;
    const amountNum = Math.abs(parseFloat(postAmount));
    if (!amountNum || Number.isNaN(amountNum)) {
      showErrorToast("Enter a valid amount");
      return;
    }

    let bucket = postAction.bucket;
    let entryType = postAction.entryType;
    let amount = amountNum;
    let paymentMethod = postPaymentMethod;
    let notes = postNotes?.trim() || null;

    if (postAction.key === "adjust") {
      bucket = adjustBucket;
      entryType = bucket === "StoreCredit" ? "StoreCreditAdjustment" : "OnAccountAdjustment";
      amount = adjustDirection === "increase" ? amountNum : -amountNum;
      paymentMethod = "Adjustment";
      if (!notes || notes.length < 3) {
        showErrorToast("Reason is required for adjustments (min 3 characters)");
        return;
      }
    }

    try {
      setSubmitting(true);
      const response = await axiosInstance.post(APIs.POST_CUSTOMER_LEDGER_ENTRY, {
        CompID,
        Cust_Guid: custGuid,
        Bucket: bucket,
        EntryType: entryType,
        Amount: amount,
        IdempotencyKey: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        PaymentMethod: paymentMethod,
        Notes: notes,
      });
      if (response.data?.status) {
        showSuccessToast(response.data.message || "Entry posted");
        setPostModalOpen(false);
        await refreshAll();
      } else {
        showErrorToast(response.data?.message || "Failed to post entry");
      }
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to post entry");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReverseSubmit = async () => {
    if (!reverseEntry || !CompID) return;
    if (!reverseReason || reverseReason.trim().length < 3) {
      showErrorToast("Reason is required (min 3 characters)");
      return;
    }
    try {
      setSubmitting(true);
      const response = await axiosInstance.post(APIs.POST_CUSTOMER_LEDGER_REVERSE, {
        CompID,
        Ledger_ID: reverseEntry.ledger_ID ?? reverseEntry.Ledger_ID,
        Reason: reverseReason.trim(),
        IdempotencyKey: `Reversal:${CompID}:${reverseEntry.ledger_ID ?? reverseEntry.Ledger_ID}`,
      });
      if (response.data?.status) {
        showSuccessToast(response.data.message || "Entry reversed");
        setReverseModalOpen(false);
        setReverseEntry(null);
        setReverseReason("");
        await refreshAll();
      } else {
        showErrorToast(response.data?.message || "Failed to reverse entry");
      }
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to reverse entry");
    } finally {
      setSubmitting(false);
    }
  };

  const customerName = wallet
    ? `${wallet.cust_Fname || wallet.Cust_Fname || ""} ${wallet.cust_Lname || wallet.Cust_Lname || ""}`.trim()
    : "";
  const customerMobile = wallet?.cust_Mobile || wallet?.Cust_Mobile || "";
  const customerEmail = wallet?.cust_Email || wallet?.Cust_Email || "";

  const onAccount = wallet?.onAccountBalance ?? wallet?.OnAccountBalance ?? 0;
  const storeCredit = wallet?.storeCreditBalance ?? wallet?.StoreCreditBalance ?? 0;
  const maxCredit = wallet?.maxCredit ?? wallet?.MaxCredit ?? 1000;
  const availableCredit = wallet?.availableCredit ?? wallet?.AvailableCredit ?? 0;

  const creditUsedPct = useMemo(() => {
    const max = Number(maxCredit) || 0;
    if (max <= 0) return 0;
    return Math.min(100, Math.max(0, (Number(onAccount) / max) * 100));
  }, [onAccount, maxCredit]);

  const summaryTiles = [
    {
      key: "due",
      label: "On Account Due",
      value: onAccount,
      icon: "fa-file-invoice",
      accent: "#dc3545",
      sub: "Customer owes restaurant",
    },
    {
      key: "credit",
      label: "Store Credit",
      value: storeCredit,
      icon: "fa-wallet",
      accent: "#0d6efd",
      sub: "Prepaid wallet balance",
    },
    {
      key: "max",
      label: "Max Credit",
      value: maxCredit,
      icon: "fa-gauge-high",
      accent: "#6c757d",
      sub: "Hard On Account limit",
    },
    {
      key: "available",
      label: "Available Credit",
      value: availableCredit,
      icon: "fa-circle-check",
      accent: themeColor,
      sub: `${creditUsedPct.toFixed(0)}% of limit used`,
    },
  ];

  if (loading && !wallet) {
    return <Preloader />;
  }

  return (
    <div className="container-fluid">
      <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item">
              <Link to="/">
                <i className="fa fa-home me-1"></i>Home
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/customer/manageCustomer">
                <i className="fa fa-users me-1"></i>Customers
              </Link>
            </li>
            <li className="breadcrumb-item active">
              <i className="fa fa-wallet me-1"></i>Wallet / Ledger
            </li>
          </ol>
          <h1 className="page-header mb-1">
            <i className="fa fa-wallet me-2" style={{ color: themeColor }}></i>
            Customer Wallet
            {customerName ? (
              <small className="ms-2 fw-normal" style={{ color: muted }}>
                {customerName}
              </small>
            ) : null}
          </h1>
          <div className="d-flex flex-wrap gap-3 small" style={{ color: muted }}>
            {customerMobile ? (
              <span>
                <i className="fa fa-phone me-1"></i>
                {customerMobile}
              </span>
            ) : null}
            {customerEmail ? (
              <span>
                <i className="fa fa-envelope me-1"></i>
                {customerEmail}
              </span>
            ) : null}
            <span>
              <i className="fa fa-list me-1"></i>
              {totalRecords} ledger {totalRecords === 1 ? "entry" : "entries"}
            </span>
          </div>
        </div>
        <div className="ms-auto d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn px-3"
            onClick={refreshAll}
            style={{
              borderRadius: "20px",
              border: `2px solid ${themeColor}`,
              color: themeColor,
              background: "transparent",
              fontWeight: 600,
            }}
          >
            <i className="fa fa-rotate me-2"></i>
            Refresh
          </button>
          <Link
            to={`/customer/updateCustomer/${custGuid}`}
            className="btn px-3"
            style={{
              borderRadius: "20px",
              border: `2px solid ${themeColor}`,
              color: themeColor,
              background: "transparent",
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            <i className="fa fa-user-pen me-2"></i>
            Edit customer
          </Link>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {summaryTiles.map((tile) => (
          <div className="col-12 col-sm-6 col-xl-3" key={tile.key}>
            <div
              className="h-100"
              style={{
                background: surface,
                border: `1px solid ${borderColor}`,
                borderLeft: `4px solid ${tile.accent}`,
                borderRadius: "12px",
                padding: "16px 18px",
                boxShadow: isDarkMode
                  ? "0 4px 18px rgba(0,0,0,0.35)"
                  : "0 4px 18px rgba(0,0,0,0.06)",
              }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div className="small text-uppercase fw-semibold" style={{ color: muted, letterSpacing: "0.04em" }}>
                  {tile.label}
                </div>
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `${tile.accent}22`,
                    color: tile.accent,
                  }}
                >
                  <i className={`fa ${tile.icon}`}></i>
                </span>
              </div>
              <div className="fs-3 fw-bold lh-1 mb-1" style={{ color: tile.accent }}>
                {formatMoney(tile.value)}
              </div>
              <div className="small" style={{ color: muted }}>
                {tile.sub}
              </div>
              {tile.key === "available" && (
                <div className="mt-3">
                  <div
                    style={{
                      height: 6,
                      borderRadius: 999,
                      background: surfaceAlt,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${creditUsedPct}%`,
                        height: "100%",
                        background: `linear-gradient(90deg, ${themeColor}, ${tile.accent})`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div
        className="mb-3"
        style={{
          background: surface,
          border: `1px solid ${borderColor}`,
          borderRadius: "12px",
          padding: "16px",
          boxShadow: isDarkMode
            ? "0 4px 18px rgba(0,0,0,0.35)"
            : "0 4px 18px rgba(0,0,0,0.06)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div className="fw-semibold">
              <i className="fa fa-bolt me-2" style={{ color: themeColor }}></i>
              Ledger actions
            </div>
            <div className="small" style={{ color: muted }}>
              All balance changes post as auditable ledger entries
            </div>
          </div>
        </div>
        <div className="row g-2">
          {POST_ACTIONS.map((action) => (
            <div className="col-12 col-md-6 col-xl" key={action.key}>
              <button
                type="button"
                className="w-100 text-start"
                onClick={() => openPostModal(action)}
                style={{
                  background: surfaceAlt,
                  border: `1px solid ${borderColor}`,
                  borderTop: `3px solid ${action.tone}`,
                  borderRadius: "10px",
                  padding: "14px 14px 12px",
                  color: isDarkMode ? "#f1f3f5" : "#212529",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = `0 8px 20px ${action.tone}33`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-1">
                  <i className={`fa ${action.icon}`} style={{ color: action.tone }}></i>
                  <span className="fw-semibold">{action.label}</span>
                </div>
                <div className="small" style={{ color: muted }}>
                  {action.hint}
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      <Panel>
        <PanelHeader>
          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between w-100">
            <span>
              <i className="fa fa-book me-2"></i>
              Ledger history
            </span>
            <span className="small opacity-75">
              Showing page {pageNumber}
              {totalPages ? ` of ${totalPages}` : ""}
            </span>
          </div>
        </PanelHeader>
        <PanelBody>
          <div className="row g-2 mb-3">
            <div className="col-md-3">
              <select
                className="form-select"
                value={bucketFilter}
                onChange={(e) => {
                  setPageNumber(1);
                  setBucketFilter(e.target.value);
                }}
              >
                <option value="">All buckets</option>
                <option value="OnAccount">On Account</option>
                <option value="StoreCredit">Store Credit</option>
              </select>
            </div>
            <div className="col-md-7">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fa fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search notes, invoice, entry type, staff..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setPageNumber(1);
                      fetchLedger();
                    }
                  }}
                />
              </div>
            </div>
            <div className="col-md-2">
              <button
                type="button"
                className="btn w-100"
                style={{
                  backgroundColor: themeColor,
                  borderColor: themeColor,
                  color: "#fff",
                  fontWeight: 600,
                }}
                onClick={() => {
                  setPageNumber(1);
                  fetchLedger();
                }}
              >
                <i className="fa fa-search me-1"></i>
                Search
              </button>
            </div>
          </div>

          {loading ? (
            <Preloader />
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: surfaceAlt }}>
                    <th>Date</th>
                    <th>Bucket</th>
                    <th>Type</th>
                    <th className="text-end">Amount</th>
                    <th className="text-end">Balance after</th>
                    <th>Payment</th>
                    <th>Invoice</th>
                    <th>Notes</th>
                    <th>By</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-5">
                        <div
                          className="mx-auto"
                          style={{
                            maxWidth: 420,
                            padding: "28px 20px",
                            borderRadius: "14px",
                            background: surfaceAlt,
                            border: `1px dashed ${borderColor}`,
                          }}
                        >
                          <div
                            className="mx-auto mb-3"
                            style={{
                              width: 56,
                              height: 56,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: `${themeColor}22`,
                              color: themeColor,
                              fontSize: 22,
                            }}
                          >
                            <i className="fa fa-book-open"></i>
                          </div>
                          <div className="fw-semibold mb-1">No ledger entries yet</div>
                          <div className="small mb-3" style={{ color: muted }}>
                            Post a payment, charge, top-up, redeem, or adjustment to start the audit trail.
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              backgroundColor: themeColor,
                              color: "#fff",
                              fontWeight: 600,
                            }}
                            onClick={() => openPostModal(POST_ACTIONS[0])}
                          >
                            <i className="fa fa-plus me-1"></i>
                            Receive first payment
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    ledger.map((row) => {
                      const id = row.ledger_ID ?? row.Ledger_ID;
                      const amount = Number(row.amount ?? row.Amount ?? 0);
                      const bal = row.balanceAfterBucket ?? row.BalanceAfterBucket ?? 0;
                      const createdAt = row.createdAt ?? row.CreatedAt;
                      const businessDate = row.businessDate ?? row.BusinessDate;
                      const bucket = row.bucket ?? row.Bucket;
                      const entryType = row.entryType ?? row.EntryType;
                      const isOnAccount = bucket === "OnAccount";
                      return (
                        <tr key={id}>
                          <td>
                            <div className="fw-semibold">
                              {businessDate
                                ? new Date(businessDate).toLocaleDateString()
                                : "-"}
                            </div>
                            <div className="small" style={{ color: muted }}>
                              {createdAt ? new Date(createdAt).toLocaleTimeString() : ""}
                            </div>
                          </td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                background: isOnAccount ? "#dc354522" : "#0d6efd22",
                                color: isOnAccount ? "#dc3545" : "#0d6efd",
                                border: `1px solid ${isOnAccount ? "#dc354544" : "#0d6efd44"}`,
                              }}
                            >
                              {bucket}
                            </span>
                          </td>
                          <td>
                            <span className="fw-semibold">{entryType}</span>
                          </td>
                          <td
                            className="text-end fw-bold"
                            style={{ color: amount >= 0 ? "#198754" : "#dc3545" }}
                          >
                            {amount >= 0 ? "+" : ""}
                            {formatMoney(amount)}
                          </td>
                          <td className="text-end">{formatMoney(bal)}</td>
                          <td>{(row.paymentMethod ?? row.PaymentMethod) || "-"}</td>
                          <td>
                            <code className="small">
                              {(row.recvorder_InvoiceNo ?? row.Recvorder_InvoiceNo) || "-"}
                            </code>
                          </td>
                          <td style={{ maxWidth: 220 }}>
                            <span className="text-truncate d-inline-block" style={{ maxWidth: 220 }}>
                              {(row.notes ?? row.Notes) || "-"}
                            </span>
                          </td>
                          <td>{(row.createdBy ?? row.CreatedBy) || "-"}</td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-sm"
                              title="Reverse this entry"
                              style={{
                                border: "1px solid #ffc107",
                                color: "#ffc107",
                                background: "transparent",
                                fontWeight: 600,
                              }}
                              onClick={() => {
                                setReverseEntry(row);
                                setReverseReason("");
                                setReverseModalOpen(true);
                              }}
                            >
                              <i className="fa fa-rotate-left me-1"></i>
                              Reverse
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {totalRecords > 0 && (
            <div className="mt-3">
              <Pagination
                totalRecords={totalRecords}
                pageSize={pageSize}
                pageNumber={pageNumber}
                totalPages={totalPages}
                setPageNumber={setPageNumber}
                setPageSize={setPageSize}
              />
            </div>
          )}
        </PanelBody>
      </Panel>

      <CustomModal
        title={postAction?.label || "Post entry"}
        isOpen={postModalOpen}
        onClose={() => setPostModalOpen(false)}
        onSubmit={handlePostSubmit}
        submitButtonText={submitting ? "Posting..." : "Post"}
        submitButtonDisabled={submitting}
        isCentered={true}
      >
        {postAction?.hint ? (
          <p className="small mb-3" style={{ color: muted }}>
            <i className={`fa ${postAction.icon} me-2`} style={{ color: postAction.tone }}></i>
            {postAction.hint}
          </p>
        ) : null}
        {postAction?.key === "adjust" && (
          <>
            <div className="mb-3">
              <label className="form-label">Bucket</label>
              <select
                className="form-select"
                value={adjustBucket}
                onChange={(e) => setAdjustBucket(e.target.value)}
              >
                <option value="OnAccount">On Account</option>
                <option value="StoreCredit">Store Credit</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Direction</label>
              <select
                className="form-select"
                value={adjustDirection}
                onChange={(e) => setAdjustDirection(e.target.value)}
              >
                <option value="increase">
                  {adjustBucket === "OnAccount" ? "Increase due (+)" : "Increase credit (+)"}
                </option>
                <option value="decrease">
                  {adjustBucket === "OnAccount" ? "Decrease due (−)" : "Decrease credit (−)"}
                </option>
              </select>
            </div>
          </>
        )}
        <FormInput
          label={`Amount (${currencySymbol})`}
          name="amount"
          type="number"
          value={postAmount}
          onchange={(e) => setPostAmount(e.target.value)}
          placeholder="0.00"
          prepend={currencySymbol}
        />
        {postAction?.key !== "adjust" && (
          <div className="mb-3 mt-2">
            <label className="form-label">Payment method</label>
            <select
              className="form-select"
              value={postPaymentMethod}
              onChange={(e) => setPostPaymentMethod(e.target.value)}
            >
              <option value="Cash">Cash</option>
              <option value="Card">Card</option>
              <option value="Online">Online</option>
              <option value="BankTransfer">Bank transfer</option>
            </select>
          </div>
        )}
        <div className="mb-2 mt-2">
          <label className="form-label">
            {postAction?.key === "adjust" ? "Reason (required)" : "Notes (optional)"}
          </label>
          <textarea
            className="form-control"
            rows={3}
            value={postNotes}
            onChange={(e) => setPostNotes(e.target.value)}
            placeholder={
              postAction?.key === "adjust"
                ? "Explain why this adjustment is needed"
                : "Optional note"
            }
          />
        </div>
      </CustomModal>

      <CustomModal
        title="Reverse ledger entry"
        isOpen={reverseModalOpen}
        onClose={() => setReverseModalOpen(false)}
        onSubmit={handleReverseSubmit}
        submitButtonText={submitting ? "Reversing..." : "Reverse"}
        submitButtonDisabled={submitting}
        isCentered={true}
      >
        {reverseEntry && (
          <p className="mb-2">
            Reverse{" "}
            <strong>{reverseEntry.entryType ?? reverseEntry.EntryType}</strong>{" "}
            of{" "}
            <strong>
              {formatMoney(reverseEntry.amount ?? reverseEntry.Amount)}
            </strong>
            ?
          </p>
        )}
        <div className="mb-2">
          <label className="form-label">Reason (required)</label>
          <textarea
            className="form-control"
            rows={3}
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
            placeholder="Why is this being reversed?"
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default CustomerWallet;
